import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useLoot } from '../hooks/useLoot';
import { useStepsContext } from '../hooks/StepsContext';
import { useGamification } from '../hooks/GamificationContext';
import { useXpBonus, XP_EVENTS } from '../hooks/useXpBonus';
import { useOptionalAppSession } from '../hooks/AppSessionContext';
import { bottleMessagesApiUrl, nonce as runtimeNonce, walletApiUrl } from '../utils/runtime';
import LootRedemptionPanel from './LootRedemptionPanel';

const XP_BOOST_IMAGE_URL = 'https://app.pfandpirat.com/wp-content/uploads/XP-Boost.png';
const BOTTLE_MESSAGE_API = bottleMessagesApiUrl || '/wp-json/pfandpirat/v1/bottle-messages';
const BOTTLE_MESSAGE_MAX_LENGTH = 300;
const INVENTORY_ASSET_IMAGE_URLS = {
  harz: 'https://app.pfandpirat.com/wp-content/uploads/harz.png',
  resin: 'https://app.pfandpirat.com/wp-content/uploads/harz.png',
  eisenbeschlage: 'https://app.pfandpirat.com/wp-content/uploads/eisenbeschlaege.png',
  eisenbeschlaege: 'https://app.pfandpirat.com/wp-content/uploads/eisenbeschlaege.png',
  ironfittings: 'https://app.pfandpirat.com/wp-content/uploads/eisenbeschlaege.png',
  holzplanken: 'https://app.pfandpirat.com/wp-content/uploads/holzplanken.png',
  woodplanks: 'https://app.pfandpirat.com/wp-content/uploads/holzplanken.png',
  nagelkiste: 'https://app.pfandpirat.com/wp-content/uploads/nagelkiste.png',
  nagelkisten: 'https://app.pfandpirat.com/wp-content/uploads/nagelkiste.png',
  nailcrate: 'https://app.pfandpirat.com/wp-content/uploads/nagelkiste.png',
  nailcrates: 'https://app.pfandpirat.com/wp-content/uploads/nagelkiste.png',
  segeltuch: 'https://app.pfandpirat.com/wp-content/uploads/segeltuch.png',
  sailcloth: 'https://app.pfandpirat.com/wp-content/uploads/segeltuch.png',
  seil: 'https://app.pfandpirat.com/wp-content/uploads/seile.avif',
  seile: 'https://app.pfandpirat.com/wp-content/uploads/seile.avif',
  seemannsgarn: 'https://app.pfandpirat.com/wp-content/uploads/seile.avif',
  rope: 'https://app.pfandpirat.com/wp-content/uploads/seile.avif',
  tauwerk: 'https://app.pfandpirat.com/wp-content/uploads/seile.avif',
};

function normalizeInventoryKey(value) {
  return String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/&[a-z0-9#]+;/gi, '')
    .replace(/[^a-z0-9]+/g, '');
}

function normalizeLootLookupKey(value) {
  return String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/&[a-z0-9#]+;/gi, '')
    .replace(/[^a-z0-9]+/g, '');
}

function stripHtml(value) {
  return String(value || '').replace(/<[^>]*>/g, '').trim();
}

function canonicalInventoryName(value) {
  const key = normalizeLootLookupKey(value);
  const aliases = {
    goldmuenzen: 'Goldmünze',
    goldmunzen: 'Goldmünze',
    goldmuenze: 'Goldmünze',
    goldmunze: 'Goldmünze',
    rostigemuenze: 'Bronzemünze',
    rostigemunze: 'Bronzemünze',
    bronzemuenze: 'Bronzemünze',
    bronzemunze: 'Bronzemünze',
    silbermuenzen: 'Silbermünze',
    silbermunzen: 'Silbermünze',
    silbermuenze: 'Silbermünze',
    silbermunze: 'Silbermünze',
    xpboost: 'XP-Boost',
    seemannsgarn: 'Seile',
    seil: 'Seile',
    rope: 'Seile',
    tauwerk: 'Seile',
  };

  return aliases[key] || String(value || '').trim();
}


function isCurrencyItemName(value) {
  const key = normalizeLootLookupKey(value);
  return [
    'bronzemuenze',
    'bronzemunze',
    'bronzemuenzen',
    'bronzemunzen',
    'silbermuenze',
    'silbermunze',
    'silbermuenzen',
    'silbermunzen',
    'goldmuenze',
    'goldmunze',
    'goldmuenzen',
    'goldmunzen',
    'rostigemuenze',
    'rostigemunze',
  ].includes(key);
}

function findLootMeta(loot, itemName) {
  const canonicalName = canonicalInventoryName(itemName);
  const wanted = normalizeLootLookupKey(canonicalName);

  return loot.find((item) => {
    const title = stripHtml(item?.title?.rendered || item?.title || '');
    const slug = item?.slug || '';
    const extra = item?.pfandpirat_loot_meta || {};

    return (
      normalizeLootLookupKey(title) === wanted ||
      normalizeLootLookupKey(slug) === wanted ||
      normalizeLootLookupKey(extra.expeditionKey || extra.expedition_key || '') === wanted
    );
  });
}

function isXpBoostItem(item) {
  return normalizeInventoryKey(item?.item_name) === 'xpboost';
}

function isBottleMessageItem(item) {
  return normalizeInventoryKey(item?.item_name) === 'alteflaschenpost';
}

function isSecretKeyItem(item) {
  return normalizeInventoryKey(item?.item_name) === 'geheimerschlussel';
}

function isTreasureChestItem(item) {
  return ['epischerschatz', 'schatztruhemini'].includes(normalizeInventoryKey(item?.item_name));
}

function isRandomLootContainerItem(item) {
  return ['pfandbeifang', 'kleineschatzkiste'].includes(normalizeInventoryKey(item?.item_name));
}

function getTreasureChestRewardLabel(item) {
  return normalizeInventoryKey(item?.item_name) === 'epischerschatz' ? '10 Gold' : '1 Gold';
}

function isStepsBoostItem(item) {
  return typeof item?.effect === 'string'
    && item.effect.replace(/\s+/g, '').toLowerCase().includes('100schritte');
}

function isUsableItem(item) {
  return Number(item?.amount || 0) > 0 && (isXpBoostItem(item) || isStepsBoostItem(item) || isBottleMessageItem(item));
}

function getInventoryFallbackImage(item) {
  const candidates = [
    item?.item_name,
    item?.slug,
    item?.effect,
    item?.effect_value,
    item?.resource,
    item?.resource_key,
    item?.type,
  ]
    .map(normalizeLootLookupKey)
    .filter(Boolean);

  for (const key of candidates) {
    if (INVENTORY_ASSET_IMAGE_URLS[key]) {
      return INVENTORY_ASSET_IMAGE_URLS[key];
    }
  }

  for (const key of candidates) {
    const matched = Object.keys(INVENTORY_ASSET_IMAGE_URLS).find((assetKey) => key.includes(assetKey));
    if (matched) {
      return INVENTORY_ASSET_IMAGE_URLS[matched];
    }
  }

  return '';
}

function getCurrentPosition() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Dein Gerät unterstützt keine Standortfreigabe.'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => resolve(position),
      () => reject(new Error('Dein Standort konnte nicht bestimmt werden. Bitte Standortfreigabe prüfen.')),
      {
        enableHighAccuracy: true,
        timeout: 12000,
        maximumAge: 0,
      }
    );
  });
}

async function parseJsonResponse(response) {
  const rawText = await response.text();

  if (!rawText) {
    return null;
  }

  try {
    return JSON.parse(rawText);
  } catch (error) {
    throw new Error('Server-Antwort ist kein sauberes JSON.');
  }
}

export default function InventoryPage() {
  const navigate = useNavigate();
  const userId = window?.pfandpiratData?.user_id;
  const nonce = window?.pfandpiratData?.nonce || runtimeNonce;
  const { addSteps } = useStepsContext();
  const { applyGamificationPayload } = useGamification();
  const { resolveXp } = useXpBonus();
  const loot = useLoot();
  const sessionContext = useOptionalAppSession();
  const bootstrapInventory = sessionContext?.session?.inventory?.items;
  const bootstrapWallet = sessionContext?.session?.wallet?.wallet || sessionContext?.session?.wallet || null;
  const hasBootstrapInventory = Array.isArray(bootstrapInventory);
  const mergeSessionPatch = sessionContext?.mergeSessionPatch;

  const [inventory, setInventory] = useState(() => (hasBootstrapInventory ? bootstrapInventory : []));
  const [wallet, setWallet] = useState(() => bootstrapWallet);
  const [error, setError] = useState(null);
  const [busyItem, setBusyItem] = useState(null);
  const [inventoryPopup, setInventoryPopup] = useState(null);
  const [isBottleModalOpen, setIsBottleModalOpen] = useState(false);
  const [bottleMessageText, setBottleMessageText] = useState('');
  const [bottleMessageSubmitting, setBottleMessageSubmitting] = useState(false);
  const [selectedBottleItem, setSelectedBottleItem] = useState(null);

  const popupTimerRef = useRef(null);
  const xpBoostReward = resolveXp(XP_EVENTS.USE_XP_BOOST, 100);

  const showInventoryPopup = useCallback((popup, duration = 3200) => {
    if (popupTimerRef.current) {
      clearTimeout(popupTimerRef.current);
    }

    setInventoryPopup(popup);

    popupTimerRef.current = setTimeout(() => {
      setInventoryPopup(null);
      popupTimerRef.current = null;
    }, duration);
  }, []);

  const closeInventoryPopup = useCallback(() => {
    if (popupTimerRef.current) {
      clearTimeout(popupTimerRef.current);
      popupTimerRef.current = null;
    }

    setInventoryPopup(null);
  }, []);

  const closeBottleModal = useCallback(() => {
    if (bottleMessageSubmitting) {
      return;
    }

    setIsBottleModalOpen(false);
    setBottleMessageText('');
    setSelectedBottleItem(null);
  }, [bottleMessageSubmitting]);


  const loadWallet = useCallback(async () => {
    if (!userId) return;

    try {
      const res = await fetch(walletApiUrl || '/wp-json/pfandpirat/v1/wallet/me', {
        credentials: 'include',
        headers: nonce ? { 'X-WP-Nonce': nonce } : {},
      });

      if (!res.ok) {
        return;
      }

      const data = await res.json();
      const nextWallet = data?.wallet || null;
      setWallet(nextWallet);
      if (mergeSessionPatch) {
        mergeSessionPatch({ wallet: { wallet: nextWallet } });
      }
    } catch (err) {
      // Wallet ist für die Inventar-Anzeige nicht kritisch.
    }
  }, [mergeSessionPatch, nonce, userId]);

  const loadInventory = useCallback(async () => {
    if (!userId) return;

    try {
      setError(null);

      const res = await fetch('/wp-json/pfandpirat/v1/inventory', {
        credentials: 'include',
        headers: nonce ? { 'X-WP-Nonce': nonce } : {},
      });

      if (!res.ok) {
        throw new Error('Nicht eingeloggt oder kein Zugriff!');
      }

      const data = await res.json();
      const nextInventory = Array.isArray(data) ? data : [];
      setInventory(nextInventory);
      if (mergeSessionPatch) {
        mergeSessionPatch({ inventory: { items: nextInventory } });
      }
    } catch (err) {
      setError(err);
    }
  }, [mergeSessionPatch, userId]);

  useEffect(() => {
    if (hasBootstrapInventory) {
      setInventory(bootstrapInventory);
      setWallet(bootstrapWallet);
      return;
    }

    loadInventory();
    loadWallet();
  }, [bootstrapInventory, bootstrapWallet, hasBootstrapInventory, loadInventory, loadWallet]);

  useEffect(() => {
    return () => {
      if (popupTimerRef.current) {
        clearTimeout(popupTimerRef.current);
      }
    };
  }, []);

  const inventoryDisplay = useMemo(() => {
    if (!Array.isArray(inventory)) return [];

    const grouped = new Map();

    for (const invItem of inventory) {
      const rawName = String(invItem.item_name || '').trim();
      const key = canonicalInventoryName(rawName);
      if (!key || isCurrencyItemName(key)) continue;

      const existing = grouped.get(key);
      if (existing) {
        existing.amount += Number(invItem.amount || 0);
        if (!existing.effect && invItem.effect) existing.effect = invItem.effect;
        if (!existing.worth && invItem.worth) existing.worth = invItem.worth;
        if (!existing.id && invItem.id) existing.id = invItem.id;
      } else {
        grouped.set(key, {
          ...invItem,
          item_name: key,
          amount: Number(invItem.amount || 0),
        });
      }
    }

    return Array.from(grouped.values()).map((invItem) => {
      const lootMeta = findLootMeta(loot, invItem.item_name);

      const lootExtra = lootMeta?.pfandpirat_loot_meta || {};

      let image = '';
      if (lootMeta?._embedded?.['wp:featuredmedia']) {
        image =
          lootMeta._embedded['wp:featuredmedia'][0]?.media_details?.sizes?.medium?.source_url ||
          lootMeta._embedded['wp:featuredmedia'][0]?.source_url ||
          '';
      }

      if (!image && lootExtra?.imageUrl) {
        image = lootExtra.imageUrl;
      }

      if (!image && lootExtra?.image_url) {
        image = lootExtra.image_url;
      }

      if (isXpBoostItem(invItem)) {
        image = XP_BOOST_IMAGE_URL;
      }

      if (!image) {
        image = getInventoryFallbackImage(invItem);
      }

      return {
        ...invItem,
        image,
        icon: lootExtra.icon || '',
        desc: lootMeta?.content?.rendered ?? '',
        rarity: lootMeta?.acf?.seltenheit ?? lootMeta?.acf?.rarity ?? lootExtra.rarity ?? '',
      };
    });
  }, [inventory, loot]);

  const hasSecretKey = useMemo(
    () => inventoryDisplay.some((item) => Number(item?.amount || 0) > 0 && isSecretKeyItem(item)),
    [inventoryDisplay]
  );

  const handleUseItem = async (item) => {
    const isChest = isTreasureChestItem(item);
    const isRandomLootContainer = isRandomLootContainerItem(item);
    if (!item?.item_name || busyItem || (!isUsableItem(item) && !isChest && !isRandomLootContainer)) return;

    if (isChest && !hasSecretKey) {
      showInventoryPopup(
        {
          type: 'error',
          icon: '⚠️',
          title: 'Schlüssel fehlt',
          lines: ['Du brauchst einen Geheimen Schlüssel, um diese Schatztruhe zu öffnen.'],
        },
        4200
      );
      return;
    }

    if (isBottleMessageItem(item)) {
      setSelectedBottleItem(item);
      setBottleMessageText('');
      setIsBottleModalOpen(true);
      return;
    }

    setBusyItem(item.item_name);

    try {
      const res = await fetch('/wp-json/pfandpirat/v1/inventory/use', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': nonce,
        },
        credentials: 'include',
        body: JSON.stringify({ item_name: item.item_name }),
      });

      const data = await parseJsonResponse(res);

      if (!res.ok || !data?.success) {
        throw new Error(data?.message || 'Konnte nicht benutzt werden.');
      }

      if (data?.session_patch && mergeSessionPatch) {
        mergeSessionPatch(data.session_patch);
      }

      if (isStepsBoostItem(item) && typeof addSteps === 'function') {
        addSteps(100);
      }

      if (Number(data?.awarded || 0) !== 0 && typeof applyGamificationPayload === 'function') {
        applyGamificationPayload(data, {
          emitXpToast: true,
          event: data?.event || XP_EVENTS.USE_XP_BOOST,
          awarded: Number(data?.awarded || 0),
          toastLabel: data?.xp_toast?.label || '',
        });
      }

      await loadInventory();
      await loadWallet();

      if (data?.inventory_popup) {
        showInventoryPopup({
          type: data.inventory_popup.type || 'success',
          icon: data.inventory_popup.icon || '🗝️',
          image: item.image || '',
          title: data.inventory_popup.title || `${item.item_name} geöffnet`,
          lines: Array.isArray(data.inventory_popup.lines)
            ? data.inventory_popup.lines
            : [data?.message || 'Die Schatztruhe wurde geöffnet.'],
        }, 4200);
      } else if (isXpBoostItem(item)) {
        const awardedXp = Number(data?.awarded || xpBoostReward || 0);
        const newXp = Number(data?.xp || 0);

        showInventoryPopup({
          type: 'success',
          icon: '🏅',
          image: XP_BOOST_IMAGE_URL,
          title: 'XP-Boost benutzt',
          lines: [
            `+${awardedXp} XP`,
            newXp > 0 ? `Neuer Stand: ${newXp} XP` : '',
          ].filter(Boolean),
        });
      } else if (isStepsBoostItem(item)) {
        showInventoryPopup({
          type: 'success',
          icon: '👣',
          title: `${item.item_name} benutzt`,
          lines: ['+100 Schritte wurden gutgeschrieben.'],
        });
      } else {
        showInventoryPopup({
          type: 'success',
          icon: '✅',
          title: `${item.item_name} benutzt`,
          lines: ['Das Item wurde erfolgreich verwendet.'],
        });
      }
    } catch (e) {
      showInventoryPopup(
        {
          type: 'error',
          icon: '⚠️',
          title: 'Aktion fehlgeschlagen',
          lines: [e.message || 'Fehler beim Benutzen.'],
        },
        4200
      );
    } finally {
      setBusyItem(null);
    }
  };

  const handlePlaceBottleMessage = useCallback(async () => {
    const trimmedMessage = bottleMessageText.trim();

    if (!selectedBottleItem || !isBottleMessageItem(selectedBottleItem)) {
      return;
    }

    if (!trimmedMessage) {
      showInventoryPopup(
        {
          type: 'error',
          icon: '⚠️',
          title: 'Nachricht fehlt',
          lines: ['Bitte gib eine Nachricht für deine Flaschenpost ein.'],
        },
        4200
      );
      return;
    }

    if (trimmedMessage.length > BOTTLE_MESSAGE_MAX_LENGTH) {
      showInventoryPopup(
        {
          type: 'error',
          icon: '⚠️',
          title: 'Nachricht zu lang',
          lines: [`Maximal ${BOTTLE_MESSAGE_MAX_LENGTH} Zeichen sind erlaubt.`],
        },
        4200
      );
      return;
    }

    setBottleMessageSubmitting(true);

    try {
      const position = await getCurrentPosition();
      const res = await fetch(BOTTLE_MESSAGE_API, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': nonce,
        },
        body: JSON.stringify({
          message: trimmedMessage,
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        }),
      });

      const data = await parseJsonResponse(res);

      if (!res.ok || !data?.success) {
        throw new Error(data?.message || 'Die Flaschenpost konnte nicht platziert werden.');
      }

      await loadInventory();
      await loadWallet();
      closeBottleModal();

      showInventoryPopup({
        type: 'success',
        icon: '🍾',
        title: 'Flaschenpost platziert',
        lines: [
          'Deine Nachricht wurde am aktuellen Ort hinterlassen.',
          'Sie bleibt bis zu 30 Tage auf der Schatzkarte aktiv.',
        ],
      }, 4200);
    } catch (e) {
      showInventoryPopup(
        {
          type: 'error',
          icon: '⚠️',
          title: 'Flaschenpost fehlgeschlagen',
          lines: [e.message || 'Die Flaschenpost konnte nicht gesendet werden.'],
        },
        4600
      );
    } finally {
      setBottleMessageSubmitting(false);
    }
  }, [bottleMessageText, closeBottleModal, loadInventory, loadWallet, nonce, selectedBottleItem, showInventoryPopup]);

  if (error) {
    return <div className="pfp-pirate-state pfp-pirate-state--error">Fehler: {error.message}</div>;
  }

  return (
    <div className="pfp-pirate-page pfp-inventory-page" style={{ padding: '1.5rem' }}>
      <Helmet>
        <title>Dein Inventar – Pfandpirat</title>
        <meta
          name="description"
          content="Dein gesammeltes Inventar im Pfandpirat-Spiel: Items, Boni und Schätze auf einen Blick. Tausche, sammle, verwalte deine Items hier!"
        />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <button
        className="pfp-pirate-back-button"
        onClick={() => navigate('/profile')}
        style={{
          background: '#0073aa',
          color: '#fff',
          border: 'none',
          borderRadius: '8px',
          padding: '0.7rem 1.4rem',
          margin: '0 0 1.5rem 0',
          fontSize: '1.1rem',
          cursor: 'pointer',
        }}
      >
        ← zurück
      </button>

      <h2>🎒 Dein Inventar</h2>

      <div
        className="pfp-inventory-wallet"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '1rem',
          maxWidth: 860,
          margin: '1rem auto 2rem',
          padding: '1rem 1.25rem',
          border: '2px solid #d9b64b',
          borderRadius: 18,
          background: 'linear-gradient(180deg, #fffdf4 0%, #fff4cf 100%)',
          boxShadow: '0 8px 24px rgba(111, 86, 18, 0.10)',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ flex: '1 1 320px' }}>
          <div style={{ fontWeight: 800, color: '#7a5306', fontSize: '1.05rem' }}>💰 Deine Pfandpirat-Münzen</div>
          <div style={{ color: '#765f18', fontSize: '0.92rem' }}>Bronze, Silber und Gold sind Anzeigeformate. Intern wird alles sauber im Wallet-Ledger geführt.</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <strong style={{ color: '#083763', fontSize: '1.08rem' }}>
            {wallet?.balance?.short_label || wallet?.balance?.label || '0 Gold · 0 Silber · 0 Bronze'}
          </strong>
          <button
            type="button"
            onClick={() => navigate('/freibeuterhafen')}
            style={{
              border: 'none',
              borderRadius: 14,
              padding: '0.72rem 1rem',
              background: 'linear-gradient(180deg, #f2b43f, #c77b0a)',
              color: '#fff',
              fontWeight: 800,
              boxShadow: '0 8px 18px rgba(174, 105, 8, 0.24)',
              cursor: 'pointer',
            }}
          >
            ⚓ Freibeuterhafen betreten
          </button>
        </div>
      </div>


      <div
        className="pfp-inventory-grid"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '2rem',
          justifyContent: 'center',
          margin: '2rem 0',
        }}
      >
        {inventoryDisplay.length === 0 && (
          <div className="pfp-pirate-empty" style={{ color: '#aaa', fontSize: '1.1rem' }}>Dein Inventar ist leer.</div>
        )}

        {inventoryDisplay.map((item) => {
          const isChest = isTreasureChestItem(item);
          const isRandomLootContainer = isRandomLootContainerItem(item);
          const showUseButton = isUsableItem(item) || isChest || isRandomLootContainer;
          const isXpBoost = isXpBoostItem(item);
          const isBottle = isBottleMessageItem(item);
          const isBusy = busyItem === item.item_name || (isBottle && bottleMessageSubmitting);

          return (
            <div
              className="pfp-inventory-item"
              key={item.item_name}
              style={{
                width: 180,
                background: '#fff',
                border: '2px solid #dbb73b',
                borderRadius: '16px',
                boxShadow: '0 2px 12px #dbc95922',
                padding: '1.2rem 1rem',
                textAlign: 'center',
              }}
            >
              {item.image ? (
                <img
                  className="pfp-inventory-item__image"
                  src={item.image}
                  alt={item.item_name}
                  style={{ width: 72, height: 72, marginBottom: 8, objectFit: 'contain' }}
                />
              ) : item.icon ? (
                <div
                  className="pfp-inventory-item__icon"
                  aria-label={item.item_name}
                  style={{
                    height: 72,
                    marginBottom: 8,
                    background: 'linear-gradient(180deg, #fff7dd 0%, #f1dd9b 100%)',
                    borderRadius: 8,
                    color: '#8a5200',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '2.35rem',
                    boxShadow: 'inset 0 0 0 1px rgba(138,82,0,0.12)',
                  }}
                >
                  {item.icon}
                </div>
              ) : (
                <div
                  className="pfp-inventory-item__placeholder"
                  style={{
                    height: 72,
                    marginBottom: 8,
                    background: '#eee',
                    borderRadius: 8,
                    color: '#888',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  Kein Bild
                </div>
              )}

              <h4 style={{ margin: '0.5rem 0 0.3rem 0', color: '#dbb73b' }}>{item.item_name}</h4>

              {item.desc && (
                <div
                  className="pfp-inventory-item__desc"
                  style={{ fontSize: '0.95rem', color: '#444', marginBottom: 6 }}
                  dangerouslySetInnerHTML={{ __html: item.desc }}
                />
              )}

              <div
                className="pfp-inventory-item__rarity"
                style={{
                  color:
                    item.rarity === 'legendär'
                      ? 'purple'
                      : item.rarity === 'episch'
                        ? 'deepskyblue'
                        : item.rarity === 'selten'
                          ? '#0073aa'
                          : '#444',
                  fontWeight: 600,
                  marginBottom: '0.3rem',
                }}
              >
                {item.rarity}
              </div>

              <div className="pfp-inventory-item__amount" style={{ fontSize: '0.82rem', color: '#a70', marginBottom: 4 }}>
                {item.amount}x im Inventar
              </div>

              {showUseButton && (
                <button
                  className="pfand-button"
                  style={{
                    marginTop: 6,
                    width: '100%',
                    opacity: isBusy ? 0.6 : 1,
                  }}
                  onClick={() => handleUseItem(item)}
                  disabled={isBusy}
                >
                  {isChest
                    ? `Öffnen (+${getTreasureChestRewardLabel(item)})`
                    : isRandomLootContainer
                      ? 'Öffnen'
                    : isXpBoost
                      ? `Benutzen (+${xpBoostReward} XP)`
                      : isBottle
                        ? 'Benutzen'
                        : 'Benutzen'}
                </button>
              )}
            </div>
          );
        })}
      </div>

      <LootRedemptionPanel />

      {isBottleModalOpen && (
        <div
          role="button"
          tabIndex={0}
          onClick={closeBottleModal}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              closeBottleModal();
            }
          }}
          aria-label="Flaschenpost-Dialog schließen"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9998,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
            background: 'rgba(18, 14, 7, 0.36)',
            backdropFilter: 'blur(3px)',
            WebkitBackdropFilter: 'blur(3px)',
          }}
        >
          <div
            onClick={(event) => event.stopPropagation()}
            style={{
              width: 'min(520px, calc(100vw - 24px))',
              background: 'linear-gradient(180deg, #fffdf3 0%, #fff4cf 100%)',
              border: '2px solid #ecd07d',
              borderRadius: 24,
              boxShadow: '0 22px 55px rgba(122, 83, 6, 0.22)',
              padding: '24px 22px 22px',
              color: '#5f4608',
            }}
          >
            <div style={{ fontSize: '1.45rem', fontWeight: 800, marginBottom: 12 }}>
              🍾 Alte Flaschenpost platzieren
            </div>

            <div style={{ fontSize: '0.98rem', lineHeight: 1.45, marginBottom: 14 }}>
              Hinterlasse eine kurze Nachricht an deinem aktuellen Ort. Beim Senden wird eine Alte Flaschenpost aus deinem Inventar verbraucht.
            </div>

            <textarea
              value={bottleMessageText}
              onChange={(event) => setBottleMessageText(event.target.value.slice(0, BOTTLE_MESSAGE_MAX_LENGTH))}
              maxLength={BOTTLE_MESSAGE_MAX_LENGTH}
              placeholder="Zum Beispiel: Viel Glück auf deiner Suche!"
              style={{
                width: '100%',
                minHeight: 140,
                borderRadius: 14,
                border: '1px solid #d3b55c',
                padding: '14px 16px',
                fontSize: '1rem',
                resize: 'vertical',
                boxSizing: 'border-box',
                background: '#fffef8',
              }}
            />

            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: 10,
                gap: 12,
                fontSize: '0.92rem',
              }}
            >
              <span>Maximal {BOTTLE_MESSAGE_MAX_LENGTH} Zeichen, nur normale Wörter und einfache Satzzeichen.</span>
              <strong>{bottleMessageText.length}/{BOTTLE_MESSAGE_MAX_LENGTH}</strong>
            </div>

            <div style={{ display: 'flex', gap: 12, marginTop: 18, flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={closeBottleModal}
                disabled={bottleMessageSubmitting}
                style={{
                  flex: '1 1 180px',
                  background: '#efe4bb',
                  color: '#6e5712',
                  border: 'none',
                  borderRadius: 12,
                  padding: '0.95rem 1rem',
                  fontWeight: 700,
                  cursor: bottleMessageSubmitting ? 'default' : 'pointer',
                  opacity: bottleMessageSubmitting ? 0.6 : 1,
                }}
              >
                Abbrechen
              </button>

              <button
                type="button"
                className="pfand-button"
                onClick={handlePlaceBottleMessage}
                disabled={bottleMessageSubmitting}
                style={{
                  flex: '1 1 220px',
                  margin: 0,
                  opacity: bottleMessageSubmitting ? 0.7 : 1,
                }}
              >
                {bottleMessageSubmitting ? 'Standort wird geprüft ...' : 'Am aktuellen Ort platzieren'}
              </button>
            </div>
          </div>
        </div>
      )}

      {inventoryPopup && (
        <div
          role="button"
          tabIndex={0}
          onClick={closeInventoryPopup}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              closeInventoryPopup();
            }
          }}
          aria-label="Popup schließen"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
            background: 'rgba(20, 16, 8, 0.28)',
            backdropFilter: 'blur(3px)',
            WebkitBackdropFilter: 'blur(3px)',
            cursor: 'pointer',
          }}
        >
          <div
            onClick={(event) => event.stopPropagation()}
            style={{
              position: 'relative',
              width: 'min(420px, calc(100vw - 32px))',
              background: inventoryPopup.type === 'error'
                ? 'linear-gradient(180deg, #fff9f8 0%, #fff1ee 100%)'
                : 'linear-gradient(180deg, #fffdf3 0%, #fff4cf 100%)',
              border: inventoryPopup.type === 'error'
                ? '2px solid #efb0a8'
                : '2px solid #ecd07d',
              borderRadius: 24,
              boxShadow: inventoryPopup.type === 'error'
                ? '0 18px 50px rgba(111, 28, 18, 0.20)'
                : '0 22px 55px rgba(122, 83, 6, 0.22)',
              padding: inventoryPopup.image ? '32px 24px 24px' : '28px 24px 22px',
              textAlign: 'center',
              color: inventoryPopup.type === 'error' ? '#8a2c23' : '#8a5200',
              cursor: 'default',
              transform: 'translateY(-2vh)',
            }}
          >
            <button
              type="button"
              onClick={closeInventoryPopup}
              aria-label="Popup schließen"
              style={{
                position: 'absolute',
                top: 12,
                right: 12,
                width: 34,
                height: 34,
                borderRadius: '50%',
                border: 'none',
                background: inventoryPopup.type === 'error' ? '#f8d8d3' : '#f4e3a8',
                color: inventoryPopup.type === 'error' ? '#8a2c23' : '#8a5200',
                fontSize: '1rem',
                fontWeight: 700,
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
              }}
            >
              ×
            </button>

            <div
              style={{
                width: 108,
                height: 108,
                borderRadius: '50%',
                margin: '0 auto 18px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: inventoryPopup.type === 'error'
                  ? 'radial-gradient(circle at 30% 30%, #fff, #ffd7d0)'
                  : 'radial-gradient(circle at 30% 30%, #fffef8, #ffe79a)',
                boxShadow: inventoryPopup.type === 'error'
                  ? '0 10px 24px rgba(190, 84, 67, 0.18)'
                  : '0 10px 24px rgba(204, 157, 39, 0.26)',
              }}
            >
              {inventoryPopup.image ? (
                <img
                  src={inventoryPopup.image}
                  alt=""
                  aria-hidden="true"
                  style={{
                    width: 76,
                    height: 76,
                    objectFit: 'contain',
                    filter: 'drop-shadow(0 6px 10px rgba(0,0,0,0.14))',
                  }}
                />
              ) : (
                <div style={{ fontSize: '2.5rem', lineHeight: 1 }}>{inventoryPopup.icon || '✅'}</div>
              )}
            </div>

            <div style={{ fontSize: '1.45rem', fontWeight: 800, marginBottom: 10 }}>{inventoryPopup.title}</div>

            {inventoryPopup.lines?.map((line, index) => (
              <div
                key={`${inventoryPopup.title}-${index}`}
                style={{
                  fontSize: index === 0 ? '1.28rem' : '1.02rem',
                  fontWeight: index === 0 ? 800 : 600,
                  opacity: index === 0 ? 1 : 0.92,
                  marginTop: index === 0 ? 0 : 8,
                }}
              >
                {line}
              </div>
            ))}

            <div
              style={{
                marginTop: 18,
                fontSize: '0.9rem',
                opacity: 0.78,
              }}
            >
              Tippe irgendwo, um das Fenster zu schließen.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
